import { compact, Dictionary, get, isArray, isEqual, sortBy, sumBy, uniqBy, uniqWith } from 'lodash';
import {
    AccountClassification,
    AccountType,
    CorporateActionsType,
    Etf,
    EtfCommonParam,
    GlobalGroupCode,
    OverExposedType,
    PositionType,
} from '../constants/lookups';
import {
    DriftReportItem,
    Earnings,
    EtfBreakdownEntry,
    EtfBreakdownWithExtraInfo,
    EtfFilterOption,
    EtfInit,
    EtfInitCommonParams,
    EtfSymbolEntryWithExtraInfo,
    HoldingInfo,
    Ipo,
    IpoWithExtraInfo,
    QuoteProfile,
    QuoteProfileWithExtraInfo,
    SectorBreakDown,
    SectorRanking,
    TiprankOverviewWithExtraInfo,
} from '../types';
import {
    Account,
    AccountHistory,
    AccountHistoryWithExtraInfo,
    OverExposedAccount,
    OverExposedEntry,
} from '../types/Account.type';
import { CorporateAction, CorporateActionWithExtraInfo } from '../types/CorporateAction.type';
import { DocumentItem, LegalDocument, LegalDocumentWithExtraInfo } from '../types/Document.type';
import { Model } from '../types/Model.type';
import { NewsArticle, NewsBodyTypes, NewsProvider } from '../types/News.type';
import { Order, OrderWithExtraInfo } from '../types/Order.type';
import { Portfolio, PortfolioWithExtraInfo, Position, PositionWithExtraInfo } from '../types/Portfolio.type';
import { MarketCapBreakdownStat } from '../types/Stat.type';
import { AccountWithExtraInfo, PositionAccount, PositionAccountWithExtraInfo } from './../types/Account.type';
import { AccountDriftReportItem, ModelDriftReportItem } from './../types/DriftReport.type';
import { ModelWithExtraInfo } from './../types/Model.type';
import { OptionChain, Quote } from './../types/Quote.type';
import { divide, multiply, subtract, sum } from './numberUtils';
import { getInverseType, getLeverageType, getSectorByIndustryCode, titleCase } from './stringUtils';

export function formatQuote(quote: Quote): Quote {
    return {
        ...quote,
        companyName: (quote.option ? quote.underlying?.companyName : quote.companyName) || '',
        lastPrice: quote.mutualFund ? quote.navPrice : quote.lastPrice,
        change: quote.mutualFund ? quote.navChange : quote?.change,
        changePercent: quote.mutualFund ? divide(quote.navChange, quote.navYesterdayPrice) : quote.changePercent,
    };
}

export function formatOrders(
    orders: Order[],
    models: Model[],
    accounts: Account[],
    inventoryAccount?: Account,
): OrderWithExtraInfo[] {
    return uniqBy(orders, (order) => order.ourRef)
        .filter((order) => !(order.accountId === inventoryAccount?.accountId && order.allocation))
        .map((order) => {
            const quote = order.quote ? formatQuote(order.quote) : undefined;
            const model = models.find((model) => model.id === order.modelId);
            const account =
                order.accountId === inventoryAccount?.accountId
                    ? inventoryAccount
                    : accounts.find((account) => account.accountId === order.accountId);

            if (order.orderLegs) {
                order.orderLegs.forEach((order) => {
                    order.quote = formatQuote(order.quote);
                });
            }

            return {
                ...order,
                quote,
                companyName: quote?.companyName || '',
                model: model?.title || 'Average Price Account',
                fullName: account?.fullName || '',
                accountNumber: account?.accountNumber || '',
                costValue: multiply(order.execPx, order.execQty) || 0,
                value: quote ? multiply(quote.lastPrice, order.execQty) : 0,
                securityType: order.option
                    ? order.orderLegs
                        ? 'Spread'
                        : 'Option'
                    : order.mutualFund
                    ? 'Mutual Fund'
                    : 'Equity',
                tradeType: order.modelId
                    ? 'Block Trade'
                    : order.allocation
                    ? 'Allocation'
                    : order.accountId === inventoryAccount?.accountId
                    ? 'Average Price Account'
                    : 'Single Account',
                total: multiply(order.expectedPx, order.quantity),
            };
        });
}

export function formatPortfolioPositions(
    positions: Position[],
    accounts: AccountWithExtraInfo[],
    inventoryAccounts: Account[],
    totalBalance: number,
    symbol?: string,
    positionType?: PositionType,
): PositionWithExtraInfo[] {
    return positions
        .filter(
            (position) => (!symbol || position.symbol === symbol) && (!positionType || position.type === positionType),
        )
        .map((position) => {
            const account = accounts.find((account) => account.accountId === position.accountId);
            const quote = formatQuote(position.quote);
            let inventoryAccount: Account | undefined;

            if (!account) {
                inventoryAccount = inventoryAccounts.find((account) => account.accountId === position.accountId);
            }

            const finalAccount = account || inventoryAccount;

            return {
                ...position,
                quote,
                fullName: finalAccount?.fullName || '',
                accountNumber: finalAccount?.accountNumber || '',
                accountType: finalAccount?.accountType || position.accountType,
                lastPrice: quote.lastPrice,
                // Dividing by zero results in Infnity or NaN
                weight: totalBalance === 0 ? 0 : divide(position.value, totalBalance),
                accountWeight: divide(position.value, account?.balance || 0),
                companyName: quote.companyName,
                marketCapClassification: quote.marketCapClassification || '',
                dividendAmount: quote.dividendAmount || 0,
                dividendYield: quote.dividendYield || 0,
                dividendExDate: quote.dividendExDate || '',
                dividendPaymentDate: quote.dividendPaymentDate || '',
                inventory: finalAccount?.accountClassification === AccountClassification.Inventory || false,
                todaysGain: multiply(subtract(quote?.lastPrice, quote.yesterdayClose), position.quantity) || 0,
                todaysGainPercent: quote.changePercent,
            };
        });
}

export function formatPortfolio(
    portfolio: Portfolio,
    accounts: AccountWithExtraInfo[],
    inventoryAccounts: Account[],
    balance: number,
    {
        symbol = undefined,
        positionType = undefined,
        tipranks = undefined,
    }: {
        symbol?: string;
        positionType?: PositionType;
        tipranks?: TiprankOverviewWithExtraInfo[];
    } = {},
): PortfolioWithExtraInfo {
    const yesterdayValue = sumBy(portfolio?.entries, (position) =>
        multiply(position?.quote?.yesterdayClose, position?.quantity),
    );
    const todaysValue = sumBy(portfolio?.entries, (position) => position.value);
    const totalTodaysGainPercent = multiply(subtract(divide(todaysValue, yesterdayValue), 1), 100) || 0;

    return {
        ...portfolio,
        entries: formatPortfolioPositions(
            portfolio.entries.map((entry) => {
                const tiprankSymbolData = symbol ? tipranks : tipranks?.find((data) => data.ticker === entry.symbol);
                return {
                    ...entry,
                    tipranks: tiprankSymbolData,
                };
            }),
            accounts,
            inventoryAccounts,
            balance,
            symbol,
            positionType,
        ),
        totalTodaysGainPercent,
        totalTodaysGain:
            sumBy(portfolio?.entries, (position) =>
                multiply(subtract(position?.quote?.lastPrice, position?.quote.yesterdayClose), position.quantity),
            ) || 0,
    };
}

export function formatMarketCapBreakdownChartData(positions: PositionWithExtraInfo[]): MarketCapBreakdownStat {
    const marketCapBreakdown = positions.reduce<Record<string, number>>((result, position) => {
        const key = position.marketCapClassification;
        if (result[key]) {
            result[key] += position.value;
        } else {
            result[key] = position.value;
        }

        return result;
    }, {});

    const total = sum(...Object.values(marketCapBreakdown));
    const labels = Object.keys(marketCapBreakdown).sort();
    const data = labels.map((key) => divide(marketCapBreakdown[key], total));

    return labels.map((label, index) => ({
        label,
        value: data[index],
    }));
}

export function formatOverExposedAccounts(
    data: OverExposedEntry[],
    overExposedType: OverExposedType,
    accounts: Account[],
): OverExposedAccount[] {
    const result = compact(
        data.map(({ accountId, concentration, quote, industryByType }) => {
            const account = accounts.find((account) => account.accountId === accountId);

            if (!account) {
                return null;
            }

            const item: OverExposedAccount = {
                overExposedType,
                accountId,
                accountNumber: account.accountNumber || '',
                fullName: account.fullName || '',
                exposure: concentration,
            };

            if (overExposedType === OverExposedType.SecurityType) {
                item.positionType = quote.securityType;
            }

            if (overExposedType === OverExposedType.Security) {
                item.symbol = quote.symbol;
            }

            if (overExposedType === OverExposedType.Industry) {
                item.sector = industryByType?.industryName;
            }

            return item;
        }),
    );

    return uniqWith(result, isEqual);
}

export function formatCorporateActions(
    data: CorporateAction[],
    positions: PositionWithExtraInfo[],
    actionTypes?: CorporateActionsType[],
): CorporateActionWithExtraInfo[] {
    const filteredData = data.filter((action) => {
        let match = true;

        if (actionTypes) {
            match = match && actionTypes.includes(action.type);
        } else {
            match = match && action.type !== CorporateActionsType.Dividend;
        }

        return match;
    });

    return filteredData.map((action) => {
        const position = positions.filter((position) => position.symbol === action?.newEntry?.symbol)?.[0] || {};

        return {
            ...action,
            symbol: action.newEntry?.symbol || '',
            companyName: action.newEntry?.companyName || '',
            quantity: position?.quantity || 0,
            exposure:
                divide(
                    position.costValue,
                    sumBy(positions, (position) => position?.costValue),
                ) || 0,
            dividendAmountPercentage: position?.dividendYield || 0,
            totalPayment: multiply(position.quantity, action.dividendAmount) || 0,
        };
    });
}

export function formatDriftReport(
    model: ModelWithExtraInfo,
    positions: Position[],
    combinedPositions: Position[],
): ModelDriftReportItem[];
export function formatDriftReport(
    account: AccountWithExtraInfo,
    positions: Position[],
    model?: ModelWithExtraInfo,
): AccountDriftReportItem[];
export function formatDriftReport(
    modelOrAccount: ModelWithExtraInfo | AccountWithExtraInfo,
    positions: Position[],
    modelOrCombinedPositions?: ModelWithExtraInfo | Position[],
): ModelDriftReportItem[] | AccountDriftReportItem[] {
    const seenPositions: Dictionary<boolean> = {};

    const forModel = isArray(modelOrCombinedPositions);
    const model = forModel
        ? (modelOrAccount as ModelWithExtraInfo)
        : (modelOrCombinedPositions as ModelWithExtraInfo | undefined);
    const account = forModel ? null : (modelOrAccount as AccountWithExtraInfo);
    const driftReportPositions = forModel ? ((modelOrCombinedPositions || []) as Position[]) : positions;
    // we can potentially have positions with different types, that's why we are combining their value
    // we will display only one security in a model, regardless it's type
    const combinedPositionsValue = driftReportPositions.reduce<Dictionary<number>>((result, position) => {
        const symbol = position.symbol;
        if (result.hasOwnProperty(symbol)) {
            result[symbol] += position.value;
        } else {
            result[symbol] = position.value;
        }

        return result;
    }, {});

    const result: DriftReportItem[] = uniqBy(
        driftReportPositions.map((position) => {
            const component = model?.components.find((component) => component.symbol === position.symbol);
            const actual = divide(
                combinedPositionsValue[position.symbol],
                forModel ? model?.balance || 0 : account?.balance || 0,
            );
            const target = component?.percentage || 0;
            const difference = subtract(target, actual);
            const quote = formatQuote(position.quote);

            seenPositions[position.symbol] = true;

            return {
                symbol: position.symbol,
                quantity: position.quantity,
                companyName: quote.companyName,
                lastPrice: quote.lastPrice,
                actual,
                target,
                difference,
                option: position.option,
                short: position.short,
                mutualFund: quote.mutualFund,
                type: position.type,
                value: combinedPositionsValue[position.symbol],
                missing: !component,
                quote,
            };
        }),
        (driftReportItem) => driftReportItem.symbol,
    );

    ((forModel ? modelOrAccount : modelOrCombinedPositions) as ModelWithExtraInfo | undefined)?.components.forEach(
        (component) => {
            if (!seenPositions.hasOwnProperty(component.symbol)) {
                const quote = formatQuote(component.quote);
                const position = positions.find((position) => position.symbol === component.symbol);

                result.push({
                    symbol: component.symbol,
                    quantity: 0,
                    companyName: quote.companyName,
                    lastPrice: quote.lastPrice || 0,
                    actual: 0,
                    target: component.percentage,
                    difference: -component.percentage,
                    option: component.option || false,
                    missing: true,
                    value: position?.value || 0,
                    short: position?.short || false,
                    type: position?.type || PositionType.Cash,
                    mutualFund: quote.mutualFund || false,
                    quote,
                });
            }
        },
    );

    result.forEach((item) => {
        if (forModel) {
            (item as ModelDriftReportItem).accountIds =
                model?.accounts
                    .filter((account) =>
                        positions.some(
                            (position) => position.accountId === account.accountId && position.symbol === item.symbol,
                        ),
                    )
                    .map((account) => account.accountId) || [];
        } else {
            (item as AccountDriftReportItem).accountType = account?.accountType || AccountType.Cash;
            (item as AccountDriftReportItem).accountBalance = account?.balance || 0;
        }
    });

    if (forModel) {
        return result as ModelDriftReportItem[];
    } else {
        return result as AccountDriftReportItem[];
    }
}

export function formatHoldingInfo(
    symbol: string,
    allPositions: Position[],
    balance: number,
    modelTarget: number,
): HoldingInfo {
    const holdingPositions = allPositions.filter((position) => position.symbol === symbol);
    const isMutualFund = holdingPositions[0].quote.mutualFund ?? false;
    const totalValue = sumBy(holdingPositions, (position) =>
        isMutualFund
            ? multiply(position.quantity, multiply(position.price, divide(position.gainPercent, 100)))
            : position.value,
    );
    const totalCostValue = sumBy(holdingPositions, (position) =>
        isMutualFund ? multiply(position.quantity, position.price) : position.costValue,
    );
    const gainPercent = sumBy(holdingPositions, (position) => position.gainPercent);

    return {
        totalValue,
        totalCostValue,
        gainPercent,
        price: holdingPositions[0]?.price,
        totalHolding: sumBy(holdingPositions, (position) => position.quantity),
        positionWeight: divide(totalValue, balance),
        modelTarget,
    };
}

export function formatPositionAccounts(
    symbol: string,
    positionAccounts: PositionAccount[],
    branchPortfolio: PortfolioWithExtraInfo,
): PositionAccountWithExtraInfo[] {
    return uniqBy(positionAccounts, (position) => position.accountId).map((positionAccount) => {
        const positions = branchPortfolio.entries.filter(
            (position) => position.accountId === positionAccount.accountId && position.symbol === symbol,
        );
        const positionValue = sumBy(positions, (position) => position.value);

        return {
            ...positionAccount,
            accountNumber: positions[0]?.accountNumber || '',
            fullName: positions[0]?.fullName || '',
            positionValue,
            positionWeight: divide(positionValue, branchPortfolio.total),
            inventory: positions[0]?.inventory || false,
        };
    });
}

export function getEtfToCompare(symbol: string) {
    if (symbol === Etf.IVV || symbol === Etf.SPY || symbol === Etf.VOO) {
        return Etf.DIA;
    }

    return Etf.SPY;
}

export function formatAccountHistory(data: AccountHistory[]): AccountHistoryWithExtraInfo[] {
    return (
        data?.map((item) => {
            const activities = Object.keys(item)
                .filter((key) => key !== 'addition' && get(item, key) === true)
                .map(titleCase);

            return {
                ...item,
                activity: activities.join(', '),
            };
        }) || []
    );
}

export const formatPassportNews = (item: any): NewsArticle => {
    return {
        ...item,
        provider: NewsProvider.Passport,
    };
};

export const formatOrbisNews = (item: any): NewsArticle => {
    return {
        article_id: item.newsId,
        body: [
            {
                type: NewsBodyTypes.Text,
                body: item.xmlBody,
            },
        ],
        release_date: item.createdOn,
        author: {
            id: 0,
            name: '',
            avatar: '',
            biography: '',
        },
        featured_image: {
            main_image: {
                url: '',
            },
            square_crop: {
                url: '',
            },
            rectangle_crop: {
                url: '',
            },
            copyright: '',
        },
        title: item.headline,
        first_paragraph: item.body,
        long_summary: item.headline,
        summary: item.headline,
        permalink: item.firstLink,
        symbols: item.symbols,
        tags: [],
        types: [],
        url: item.firstLink,
        provider: item.provider,
    };
};

export function formatOptionChain(quotes: Quote[]): OptionChain {
    return quotes.reduce<OptionChain>((result, quote) => {
        const rootSymbol = quote.rootSymbol;
        const strikePrice = quote.strikePrice;

        if (!result.hasOwnProperty(rootSymbol)) {
            result[rootSymbol] = {};
        }

        if (!result[rootSymbol].hasOwnProperty(strikePrice)) {
            result[rootSymbol][strikePrice] = {};
        }

        if (quote.call) {
            result[rootSymbol][strikePrice].call = quote;
        } else {
            result[rootSymbol][strikePrice].put = quote;
        }

        return result;
    }, {});
}

export const formatEtfBySector = (
    data: SectorBreakDown,
    industryGroupCode: GlobalGroupCode,
): EtfBreakdownWithExtraInfo[] => {
    return (
        data?.[getSectorByIndustryCode(industryGroupCode)].map((item) => {
            return {
                ...item,
                symbol: item?.symbol || '',
                companyName: item?.quote?.companyName || '',
                lastPrice: item?.quote?.lastPrice || 0,
                changePercent: item?.quote?.changePercent || 0,
            };
        }) || []
    );
};

export const getSectorIndustryByIndustryGroupCode = (data: SectorRanking[], industryGroupCode: GlobalGroupCode) =>
    data.filter((item) => String(item.industry.levelCode).startsWith(String(industryGroupCode)));

export const formatQuoteProfile = (data: QuoteProfile): QuoteProfileWithExtraInfo => {
    const fullAddress =
        `${data?.address?.streets?.join(' ')}, ${data?.address?.city} ${data?.address?.state}, ${
            data?.address?.zipcode
        } ${data?.address?.country}` || '';

    return {
        ...data,
        fullAddress,
        sector: data?.globalGroup || '',
        industry: data?.globalIndustry || '',
        description: data?.longSummary || data?.businessSummary || '',
        url: data?.url || data?.etfSponsorUrl || '',
        intOwnership: data?.institutionalOwnPc || 0,
        insOwnership: data?.insiderOwnPct || 0,
        employees: data?.employeeCount || 0,
        etfCategory: data?.etfSymbolEntry?.category || '',
        etfAsset: data?.etfSymbolEntry?.asset || '',
        expenseRatio: data?.etfSymbolEntry?.expenseRatio || 0,
        asset: data?.etfSymbolEntry?.asset || '',
        category: data?.etfSymbolEntry?.category || '',
        leverage: data?.etfSymbolEntry?.leverage || 0,
        inverse: data?.etfSymbolEntry?.inverse || false,
        structure: data?.etfSymbolEntry?.structure || '',
        sponsor: data?.etfSymbolEntry?.sponsor || '',
    };
};

export const formatIpo = (data: Ipo): IpoWithExtraInfo => ({
    ...data,
    offerPrice:
        divide(data.offerPriceMin + data.offerPriceMax, 2) !== 0
            ? divide(data.offerPriceMin + data.offerPriceMax, 2)
            : data.offerPrice,
    ipoReturn: data.totalReturn,
    lastPrice: data.quote?.lastPrice || 0,
    change: data.quote?.change || 0,
    ipoSize: multiply(
        multiply(
            data.offerShares,
            divide(data.offerPriceMin + data.offerPriceMax, 2) !== 0
                ? divide(data.offerPriceMin + data.offerPriceMax, 2)
                : data.offerPrice,
        ),
        1000000,
    ),
    quoteClose: data.quoteYesterdayCloseWithoutSplit,
});

export const formatEtfSymbolEntryWithExtraInfo = (data: EtfBreakdownEntry): EtfSymbolEntryWithExtraInfo => ({
    ...data.etf,
    symbol: data.quote.symbol || '',
    companyName: data.quote.companyName || '',
    size: data.quote.marketCap,
    volume: data.quote.volume || 0,
    change: data.quote.change || 0,
    changePercent: data.quote.changePercent || 0,
    price: data.quote.lastPrice || 0,
});

export const formatUpcomingEarnings = (data: Earnings) => {
    const quote = formatQuote(data?.quote || '');
    return {
        ...data,
        symbol: data.symbol || '',
        companyName: quote?.companyName || '',
        volume: quote?.volume || 0,
        change: quote?.change || 0,
        changePercent: quote?.changePercent || 0,
        price: quote?.lastPrice || 0,
        marketCap: quote?.marketCap || 0,
    };
};

export const formatEtfInitCommonParams = (data: EtfInit): EtfInitCommonParams => {
    const sponsors: EtfFilterOption[] = Object.values(data.Sponsors).map((item, index) => ({
        value: item.name,
        id: Object.keys(data.Sponsors)[index],
    }));
    const categories: EtfFilterOption[] = Object.values(data.Categories).map((item, index) => ({
        value: item,
        id: Object.keys(data.Categories)[index],
    }));
    const assets: EtfFilterOption[] = Object.values(data.AssetClasses).map((item, index) => ({
        value: item,
        id: Object.keys(data.AssetClasses)[index],
    }));
    const leverage: EtfFilterOption[] = Object.values(data.LeverageTypes).map((item, index) => ({
        value: item,
        id: Object.keys(data.LeverageTypes)[index],
        name: getLeverageType(item),
    }));
    const inverse: EtfFilterOption[] = Object.values(data.InverseTypes).map((item) => ({
        value: item,
        name: getInverseType(item),
    }));

    return {
        [EtfCommonParam.Sponsors]: sortBy(sponsors, (sponsor) => sponsor.value),
        [EtfCommonParam.Categories]: sortBy(categories, (category) => category.value),
        [EtfCommonParam.AssetClasses]: sortBy(assets, (asset) => asset.value),
        [EtfCommonParam.LeverageTypes]: sortBy(leverage, (asset) => asset.value),
        [EtfCommonParam.InverseTypes]: sortBy(inverse, (asset) => asset.value),
    };
};

export const formatDocumentData = (data?: DocumentItem[]): DocumentItem[] => {
    return (
        data?.map((item) => {
            return {
                ...item,
                documentDate: item.documentDate || item.tradeDate,
                documentType: item.documentType || item.reportType,
                customType:
                    item.accountNumber +
                    '_' +
                    (item.documentType || item.reportType) +
                    '_' +
                    (item.documentDate || item.tradeDate),
                documentDownload: item.url || item.link,
            };
        }) || []
    );
};

export const formatLegalDocumentData = (data: LegalDocument[]): LegalDocumentWithExtraInfo[] => {
    return data.map((item: LegalDocument) => {
        return {
            name: item.revision.split('|')[0],
            ...item,
        };
    });
};
